import React from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import _ from '@/utils/lodash';
import cx from 'classnames';
import { useAppContext } from '@/context/AppContext';
import { useMasonryColumns } from '@/masonry';
import CroppedImage, { getCroppedAspectRatio, normalizeCrop } from '@components/shared/CroppedImage';
import DownloadOriginalButton from '@components/shared/DownloadOriginalButton';
import {
    AnalyticsMetricStack,
    ImageTileActionButtons,
    DescriptionInput,
} from '@components/preview/masonry/PreviewMasonryChrome';
import { usePreviewImageAnalytics } from '@components/preview/previewAnalytics';
import { hasAnalyticsData } from '@/analytics/metrics';
import { useDragEdgeScroll } from './dragEdgeScroll';
import { setScaledDragImage } from './dragImage';
import { galleryGapCss } from '@/blocks/spacing';
import { galleryDeviceLayout } from '@/blocks/gallery-devices';
import useGalleryDevice from '@/hooks/useGalleryDevice';
import { getMediaType, getPosterPaths, getVideoUrl, toPublicMediaUrl } from '@/utils/media';
import { EDITORIAL_GALLERY_MESSAGE, GALLERY_SELECTION_MESSAGE, MASONRY_UPLOAD_STATUS } from '@/blocks/preview';
import { frameFileTools, useEditorFrame } from '@components/preview/editGates';

const DROP_ID = '__masonry-drop__';

// Tiles whose picture already loaded this visit. A page re-mounted when the
// viewer closes must paint those at once, not as placeholders that fade in.
const loadedTiles = new Set();

function dragHasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
}

function isGalleryUpload(file) {
    const type = file?.type || '';
    if (type.startsWith('image/') || type.startsWith('video/')) return true;
    return /\.(jpe?g|png|gif|webp|avif|heic|heif|mov|mp4|m4v|webm)$/i.test(file?.name || '');
}

/** Caption field and tile tools keep their own clicks; the rest of the tile can be picked. */
function isTileControl(target) {
    return Boolean(target?.closest?.('[data-action-button], [data-tile-control], input, textarea, button'));
}

function readUploadSize(file) {
    return new Promise((resolve) => {
        if (!file || String(file.type || '').startsWith('video/')) {
            resolve({ width: 16, height: 9 });
            return;
        }
        const url = URL.createObjectURL(file);
        const img = new Image();
        const finish = (size) => {
            URL.revokeObjectURL(url);
            resolve(size);
        };
        img.onload = () => finish({
            width: img.naturalWidth || 1,
            height: img.naturalHeight || 1,
        });
        img.onerror = () => finish({ width: 1, height: 1 });
        img.src = url;
    });
}

/** Browsers draw these in an <img>; HEIC and video get a plain placeholder. */
function canPreview(file) {
    const type = String(file?.type || '');
    return type.startsWith('image/') && !/hei[cf]/i.test(type);
}

function uploadLabel(upload) {
    if (upload.phase === 'uploading') return `Uploading ${Math.round(upload.progress || 0)}%`;
    if (upload.phase === 'processing' || upload.phase === 'done') return 'Processing…';
    return 'Waiting…';
}

function DropTile({ label, previewUrl, dimensions, beforeId = null, progress = null }) {
    const ratio = dimensions?.width && dimensions?.height
        ? `${dimensions.width} / ${dimensions.height}`
        : '1 / 1';
    return (
        <figure className="masonry-item m-0" data-drop-placeholder="true" data-before-id={beforeId || ''}>
            <div
                className="relative flex w-full items-center justify-center overflow-hidden border-2 border-dashed"
                style={{
                    aspectRatio: ratio,
                    borderColor: 'color-mix(in srgb, var(--color-text) 45%, transparent)',
                    background: 'color-mix(in srgb, var(--color-text) 8%, transparent)',
                }}
            >
                {previewUrl ? (
                    <img src={previewUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
                ) : null}
                <span className={cx(
                    'relative px-3 text-center text-xs font-medium',
                    previewUrl && 'bg-white/80 py-1.5 text-gray-900',
                )}>
                    {label}
                </span>
                {progress !== null ? (
                    <span className="absolute inset-x-2 bottom-2 h-1.5 overflow-hidden rounded-full bg-white/70">
                        <span
                            className={cx('block h-full bg-[#2563eb] transition-[width] duration-200', progress >= 100 && 'animate-pulse')}
                            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                        />
                    </span>
                ) : null}
            </div>
        </figure>
    );
}

DropTile.propTypes = {
    label: PropTypes.string,
    previewUrl: PropTypes.string,
    dimensions: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    /** The image a file dropped on this placeholder goes before. */
    beforeId: PropTypes.string,
    /** 0–100 while the file uploads; null hides the bar. */
    progress: PropTypes.number,
};

/**
 * Masonry Grid using Column Distribution Algorithm
 * 
 * Uses the canonical masonry algorithm to distribute images into columns.
 * Each image is placed in the column with the smallest cumulative height.
 * Columns are rendered using flexbox for natural document flow.
 * 
 * Column counts and breakpoints can be customized via database:
 * - `columns`: Base column count (2-6)
 * - `breakpoints`: Custom responsive breakpoints (optional)
 */
function placeBefore(ids, fromId, toId) {
    if (!fromId || fromId === toId) return ids;
    const next = ids.filter((id) => id !== fromId);
    const at = next.indexOf(toId);
    next.splice(at < 0 ? next.length : at, 0, fromId);
    return next;
}

function ImagesMasonry({ images, url, hide, columns: baseColumns, breakpoints: customBreakpoints, gap: baseGap = 4, devices = null, crops = null, arrange = false, onArrange, onTileEdit, onFileDrop }) {
    const { content, theme } = useAppContext();
    const containerRef = React.useRef(null);
    const inFrame = useEditorFrame();
    const device = useGalleryDevice(theme?.layout?.pageWidth);
    // The slot shown while files are dragged over the grid: { beforeId }.
    const [dragSlot, setDragSlot] = React.useState(null);
    const dragSlotRef = React.useRef(null);
    dragSlotRef.current = dragSlot;
    // One placeholder per dropped file, in drop order:
    // { key, beforeId, dimensions, previewUrl, phase, progress, id }.
    const [uploads, setUploads] = React.useState([]);
    const uploadsRef = React.useRef(uploads);
    uploadsRef.current = uploads;
    
    // Get image data with dimensions
    const imageData = React.useMemo(() => {
        if (!images || !content?.files) return [];
        return images
            .map(im => _.result(content, ['files', im], null))
            .filter(im => im && im.dimensions);
    }, [images, content]);

    const layout = galleryDeviceLayout({
        columns: baseColumns,
        breakpoints: customBreakpoints,
        gap: baseGap,
        devices,
    })[device];
    const gap = layout.gap;

    // Height estimation function for masonry distribution
    // A cropped tile is drawn at its crop's shape, so it is measured by it too.
    const getItemHeight = React.useCallback((item) => {
        if (!item?.dimensions?.width || !item?.dimensions?.height) {
            return 1; // Default to square
        }
        const crop = !item.placeholder ? normalizeCrop(crops?.[item.id]) : null;
        const width = item.dimensions.width * (crop?.width || 1);
        const height = item.dimensions.height * (crop?.height || 1);
        return width > 0 ? height / width : 1;
    }, [crops]);

    // Distribute images into columns (uses database columns and breakpoints)
    // Placeholders sit before the image they were dropped on. The editor adds
    // each finished file there too, so the ones still uploading stay after it.
    const layoutItems = React.useMemo(() => {
        if (!dragSlot && !uploads.length) return imageData;
        const shown = new Set(imageData.map((item) => item.id));
        let list = imageData;
        const place = (tile, beforeId) => {
            const at = beforeId ? list.findIndex((item) => item.id === beforeId) : -1;
            list = at < 0 ? [...list, tile] : [...list.slice(0, at), tile, ...list.slice(at)];
        };
        uploads.forEach((upload) => {
            if (upload.id && shown.has(upload.id)) return;
            place({
                id: `${DROP_ID}:${upload.key}`,
                placeholder: true,
                upload,
                dimensions: upload.dimensions || { width: 1, height: 1 },
            }, upload.beforeId);
        });
        if (dragSlot) {
            place({ id: DROP_ID, placeholder: true, dimensions: { width: 1, height: 1 } }, dragSlot.beforeId);
        }
        return list;
    }, [imageData, uploads, dragSlot]);

    const { columns, columnCount } = useMasonryColumns({
        items: layoutItems,
        containerRef,
        getItemHeight,
        columnCount: layout.columns,
    });

    // Tiles picked while arranging, in gallery order. The editor shows what can
    // be done with them and asks for the selection to be cleared afterwards.
    // The anchor is the last tile clicked or dragged, whether or not that click
    // changed the selection. Shift-click adds every tile from that anchor through
    // the one under the pointer. ⌘/Ctrl-click toggles just that tile.
    const [picked, setPicked] = React.useState([]);
    const pickedRef = React.useRef(picked);
    pickedRef.current = picked;
    const lastPickRef = React.useRef(null);
    const reportedRef = React.useRef('[]');
    const order = React.useMemo(() => imageData.map((item) => item.id), [imageData]);
    // With several tiles picked, typing in any one of their captions types in
    // all of them: { ids, text }, the tiles picked when it was typed. Null
    // until the user types; a new selection starts over.
    const [sharedCaption, setSharedCaption] = React.useState(null);
    const sharedCaptionRef = React.useRef(null);
    sharedCaptionRef.current = sharedCaption;
    // The tiles the shared caption was typed for, while it is not saved yet.
    const unsavedSharedRef = React.useRef(null);
    const onTileEditRef = React.useRef(onTileEdit);
    onTileEditRef.current = onTileEdit;

    // Picking or unpicking a tile does not take focus from the caption, so
    // a shared caption typed so far is saved for the tiles it was typed for.
    React.useEffect(() => {
        const unsaved = unsavedSharedRef.current;
        unsavedSharedRef.current = null;
        if (unsaved) {
            onTileEditRef.current?.({
                action: 'ui-description',
                kind: 'image',
                id: unsaved.ids[0],
                ids: unsaved.ids,
                description: unsaved.text,
            });
        }
        setSharedCaption(null);
    }, [picked]);

    const typeCaption = React.useCallback((id, text) => {
        const ids = pickedRef.current;
        if (ids.length < 2 || !ids.includes(id)) return;
        unsavedSharedRef.current = { ids, text };
        setSharedCaption({ ids, text });
    }, []);

    const commitCaption = React.useCallback((id, text) => {
        const ids = sharedCaptionRef.current?.ids || [];
        const shared = ids.length > 1 && ids.includes(id);
        if (shared) unsavedSharedRef.current = null;
        onTileEdit?.({
            action: 'ui-description',
            kind: 'image',
            id,
            ...(shared ? { ids } : null),
            description: text,
        });
    }, [onTileEdit]);

    React.useEffect(() => {
        if (!arrange) {
            setPicked([]);
            lastPickRef.current = null;
        }
    }, [arrange]);

    React.useEffect(() => {
        setPicked((current) => {
            const kept = current.filter((id) => order.includes(id));
            return kept.length === current.length ? current : kept;
        });
    }, [order]);

    React.useEffect(() => {
        const key = JSON.stringify(picked);
        if (key === reportedRef.current) return;
        reportedRef.current = key;
        onTileEdit?.({ action: 'ui-select', kind: 'image', ids: picked });
    }, [picked, onTileEdit]);

    React.useEffect(() => {
        if (!inFrame) return undefined;
        const onClear = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === GALLERY_SELECTION_MESSAGE) setPicked([]);
        };
        window.addEventListener('message', onClear);
        return () => window.removeEventListener('message', onClear);
    }, [inFrame]);

    // Esc clears the picked tiles before it leaves the block. A caption being
    // typed is saved first.
    React.useEffect(() => {
        if (!picked.length) return undefined;
        const onKey = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            event.preventDefault();
            event.stopPropagation();
            const active = document.activeElement;
            if (active && containerRef.current?.contains(active)) active.blur();
            setPicked([]);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [picked.length]);

    const noteTile = React.useCallback((id) => {
        lastPickRef.current = id;
    }, []);

    const togglePick = React.useCallback((id, range) => {
        const anchor = lastPickRef.current;
        lastPickRef.current = id;
        setPicked((current) => {
            let change = [id];
            const from = range ? order.indexOf(anchor) : -1;
            if (from >= 0) {
                const to = order.indexOf(id);
                if (to >= 0) change = order.slice(Math.min(from, to), Math.max(from, to) + 1);
            }
            const next = new Set(current);
            if (range) change.forEach((key) => next.add(key));
            else if (next.has(id)) next.delete(id);
            else next.add(id);
            return order.filter((key) => next.has(key));
        });
    }, [order]);

    const dropUploads = React.useCallback((keys) => {
        const gone = uploadsRef.current.filter((upload) => keys.includes(upload.key));
        if (!gone.length) return;
        gone.forEach((upload) => {
            if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
        });
        setUploads((current) => current.filter((upload) => !keys.includes(upload.key)));
    }, []);

    React.useEffect(() => {
        if (!inFrame) return undefined;
        const onStatus = (event) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data;
            if (data?.type !== MASONRY_UPLOAD_STATUS || !data.key) return;
            if (data.phase === 'error') {
                dropUploads([data.key]);
                return;
            }
            setUploads((current) => {
                const at = current.findIndex((upload) => upload.key === data.key);
                if (at < 0) return current;
                const next = current.slice();
                next[at] = {
                    ...current[at],
                    phase: data.phase || current[at].phase,
                    progress: Number(data.progress) || 0,
                    id: data.id || current[at].id || null,
                };
                return next;
            });
        };
        window.addEventListener('message', onStatus);
        return () => window.removeEventListener('message', onStatus);
    }, [inFrame, dropUploads]);

    // A finished file leaves its placeholder once its own tile is in the grid,
    // or shortly after when the draft no longer holds it.
    React.useEffect(() => {
        const finished = uploads.filter((upload) => upload.phase === 'done' && upload.id);
        if (!finished.length) return undefined;
        const shown = new Set(imageData.map((item) => item.id));
        const settled = finished.filter((upload) => shown.has(upload.id)).map((upload) => upload.key);
        if (settled.length) dropUploads(settled);
        const waiting = finished.filter((upload) => !shown.has(upload.id)).map((upload) => upload.key);
        if (!waiting.length) return undefined;
        const timer = setTimeout(() => dropUploads(waiting), 2000);
        return () => clearTimeout(timer);
    }, [uploads, imageData, dropUploads]);

    React.useEffect(() => () => {
        uploadsRef.current.forEach((upload) => {
            if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
        });
    }, []);

    const beforeIdFromEvent = (event) => {
        const slot = event.target?.closest?.('[data-drop-placeholder]');
        if (slot) return slot.getAttribute('data-before-id') || dragSlotRef.current?.beforeId || null;
        const id = event.target?.closest?.('[data-image-id]')?.getAttribute('data-image-id');
        return id || null;
    };

    const showDropSlot = (event) => {
        const beforeId = beforeIdFromEvent(event);
        setDragSlot((prev) => (prev && prev.beforeId === beforeId ? prev : { beforeId }));
    };

    const takeDroppedFiles = (event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter(isGalleryUpload);
        const beforeId = beforeIdFromEvent(event);
        setDragSlot(null);
        if (!files.length) return;
        const batch = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const keys = files.map((_, index) => `${batch}-${index}`);
        setUploads((current) => [...current, ...files.map((file, index) => ({
            key: keys[index],
            beforeId,
            dimensions: null,
            previewUrl: canPreview(file) ? URL.createObjectURL(file) : null,
            phase: 'waiting',
            progress: 0,
            id: null,
        }))]);
        files.forEach((file, index) => {
            readUploadSize(file).then((dimensions) => {
                setUploads((current) => current.map((upload) => (
                    upload.key === keys[index] ? { ...upload, dimensions } : upload
                )));
            });
        });
        const payload = { files, beforeId, keys };
        if (onFileDrop) {
            onFileDrop(payload);
            return;
        }
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({
                type: EDITORIAL_GALLERY_MESSAGE,
                action: 'upload',
                files,
                beforeId,
                keys,
            }, window.location.origin);
        }
    };

    // Keyboard navigation: arrow keys work like Tab/Shift+Tab (left/right) or move vertically (up/down)
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            // Check if this is an arrow key we handle
            const isArrowKey = e.keyCode === 37 || e.keyCode === 38 || e.keyCode === 39 || e.keyCode === 40;
            if (!isArrowKey) return;
            
            const activeElement = document.activeElement;
            const isImageFocused = activeElement && activeElement.tagName === 'A' && activeElement.closest('.masonry-item');
            
            // If no image is focused and user presses arrow key, focus image in middle of viewport
            if (!isImageFocused) {
                e.preventDefault();
                
                // Get all image links with their viewport positions
                const allImageLinks = Array.from(containerRef.current?.querySelectorAll('.masonry-item a') || [])
                    .map(link => {
                        const rect = link.getBoundingClientRect();
                        const viewportCenterY = window.innerHeight / 2;
                        const viewportCenterX = window.innerWidth / 2;
                        return {
                            element: link,
                            tabIndex: parseInt(link.getAttribute('tabindex') || '0', 10),
                            centerX: rect.left + rect.width / 2,
                            centerY: rect.top + rect.height / 2,
                            // Distance from viewport center
                            distanceFromCenter: Math.sqrt(
                                Math.pow(rect.left + rect.width / 2 - viewportCenterX, 2) +
                                Math.pow(rect.top + rect.height / 2 - viewportCenterY, 2)
                            ),
                        };
                    });
                
                if (allImageLinks.length === 0) return;
                
                // Find the image closest to the center of the viewport
                const closestToCenter = allImageLinks.reduce((best, candidate) => 
                    candidate.distanceFromCenter < best.distanceFromCenter ? candidate : best
                );
                
                if (closestToCenter) {
                    closestToCenter.element.focus();
                }
                return;
            }
            
            // Get all image links with their positions
            const allImageLinks = Array.from(containerRef.current?.querySelectorAll('.masonry-item a') || [])
                .map(link => {
                    const rect = link.getBoundingClientRect();
                    const containerRect = containerRef.current?.getBoundingClientRect();
                    return {
                        element: link,
                        tabIndex: parseInt(link.getAttribute('tabindex') || '0', 10),
                        top: rect.top - (containerRect?.top || 0),
                        bottom: rect.bottom - (containerRect?.top || 0),
                        left: rect.left - (containerRect?.left || 0),
                        right: rect.right - (containerRect?.left || 0),
                        centerX: rect.left + rect.width / 2 - (containerRect?.left || 0),
                        centerY: rect.top + rect.height / 2 - (containerRect?.top || 0),
                    };
                });
            
            if (allImageLinks.length === 0) return;
            
            const currentRect = activeElement.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect();
            const currentTop = currentRect.top - (containerRect?.top || 0);
            const currentBottom = currentRect.bottom - (containerRect?.top || 0);
            const currentLeft = currentRect.left - (containerRect?.left || 0);
            const currentRight = currentRect.right - (containerRect?.left || 0);
            const currentCenterX = currentRect.left + currentRect.width / 2 - (containerRect?.left || 0);
            
            let targetElement = null;
            
            if (e.keyCode === 37) { // Left arrow (previous, like Shift+Tab)
                e.preventDefault();
                const sorted = allImageLinks.sort((a, b) => a.tabIndex - b.tabIndex);
                const currentIndex = sorted.findIndex(item => item.element === activeElement);
                if (currentIndex !== -1) {
                    const targetIndex = currentIndex > 0 ? currentIndex - 1 : sorted.length - 1;
                    targetElement = sorted[targetIndex].element;
                }
            } else if (e.keyCode === 39) { // Right arrow (next, like Tab)
                e.preventDefault();
                const sorted = allImageLinks.sort((a, b) => a.tabIndex - b.tabIndex);
                const currentIndex = sorted.findIndex(item => item.element === activeElement);
                if (currentIndex !== -1) {
                    const targetIndex = currentIndex < sorted.length - 1 ? currentIndex + 1 : 0;
                    targetElement = sorted[targetIndex].element;
                }
            } else if (e.keyCode === 38) { // Up arrow - find image directly above
                e.preventDefault();
                // Find images that are above the current one
                const candidatesAbove = allImageLinks.filter(item => 
                    item.element !== activeElement && 
                    item.bottom <= currentTop
                );
                
                if (candidatesAbove.length > 0) {
                    // Find the one closest horizontally (prefer same column)
                    // Then pick the one with the largest bottom (closest to current top)
                    targetElement = candidatesAbove.reduce((best, candidate) => {
                        const bestHorizontalDist = Math.abs(best.centerX - currentCenterX);
                        const candidateHorizontalDist = Math.abs(candidate.centerX - currentCenterX);
                        
                        // Prefer images in the same column (small horizontal distance)
                        if (candidateHorizontalDist < bestHorizontalDist) {
                            return candidate;
                        } else if (candidateHorizontalDist === bestHorizontalDist) {
                            // If same horizontal distance, prefer the one closer vertically
                            return candidate.bottom > best.bottom ? candidate : best;
                        }
                        return best;
                    }).element;
                }
            } else if (e.keyCode === 40) { // Down arrow - find image directly below
                e.preventDefault();
                // Find images that are below the current one
                const candidatesBelow = allImageLinks.filter(item => 
                    item.element !== activeElement && 
                    item.top >= currentBottom
                );
                
                if (candidatesBelow.length > 0) {
                    // Find the one closest horizontally (prefer same column)
                    // Then pick the one with the smallest top (closest to current bottom)
                    targetElement = candidatesBelow.reduce((best, candidate) => {
                        const bestHorizontalDist = Math.abs(best.centerX - currentCenterX);
                        const candidateHorizontalDist = Math.abs(candidate.centerX - currentCenterX);
                        
                        // Prefer images in the same column (small horizontal distance)
                        if (candidateHorizontalDist < bestHorizontalDist) {
                            return candidate;
                        } else if (candidateHorizontalDist === bestHorizontalDist) {
                            // If same horizontal distance, prefer the one closer vertically
                            return candidate.top < best.top ? candidate : best;
                        }
                        return best;
                    }).element;
                }
            }
            
            if (targetElement) {
                targetElement.focus();
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [imageData]);

    const [overId, setOverId] = React.useState(null);
    const dragRef = React.useRef(null);
    const orderIds = imageData.map((item) => item.id);
    useDragEdgeScroll(arrange, containerRef);

    const dropOn = (targetId) => {
        const fromId = dragRef.current;
        dragRef.current = null;
        setOverId(null);
        if (!arrange || !onArrange || !fromId) return;
        const next = placeBefore(orderIds, fromId, targetId);
        if (next.join('\0') !== orderIds.join('\0')) onArrange(next);
    };

    if (!layoutItems.length && !inFrame) return null;

    const gapCss = galleryGapCss(gap, columnCount);

    return (
        <section 
            ref={containerRef}
            className={cx(
                'masonry-container page-column flex transition-opacity duration-200',
                { 'opacity-0': hide }
            )}
            style={{
                gap: gapCss,
            }}
            role="feed"
            aria-label="Image gallery"
            data-page-path="page.grid"
            onDragEnter={(event) => {
                if (!inFrame || !dragHasFiles(event)) return;
                event.preventDefault();
                showDropSlot(event);
            }}
            onDragOver={(event) => {
                if (!inFrame || !dragHasFiles(event)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                showDropSlot(event);
            }}
            onDragLeave={(event) => {
                if (containerRef.current?.contains(event.relatedTarget)) return;
                setDragSlot(null);
            }}
            onDrop={(event) => {
                if (!inFrame || !dragHasFiles(event)) return;
                event.preventDefault();
                takeDroppedFiles(event);
            }}
        >
            {!layoutItems.length && inFrame ? (
                <p className="w-full py-16 text-center text-sm opacity-60">Drop an image to upload</p>
            ) : columns.map((column, colIndex) => (
                <div 
                    key={colIndex} 
                    className="masonry-column flex-1 flex flex-col min-w-0"
                    style={{ gap: gapCss }}
                >
                    {column.map(({ item, originalIndex }) => (
                        item.placeholder ? (
                            <DropTile
                                key={item.id}
                                label={item.upload ? uploadLabel(item.upload) : 'Drop here'}
                                previewUrl={item.upload?.previewUrl || null}
                                dimensions={item.dimensions}
                                beforeId={item.upload ? item.upload.beforeId : dragSlot?.beforeId}
                                progress={item.upload && item.upload.phase !== 'waiting'
                                    ? (item.upload.phase === 'uploading' ? item.upload.progress : 100)
                                    : null}
                            />
                        ) : (
                        <MasonryImage 
                            key={item.id} 
                            file={item}
                            pageUrl={url} 
                            tabIndex={originalIndex + 1}
                            crop={crops?.[item.id] || null}
                            arrange={arrange}
                            over={overId === item.id}
                            picked={picked.includes(item.id)}
                            sharedCaption={sharedCaption?.ids.includes(item.id) ? sharedCaption.text : null}
                            onCaptionType={typeCaption}
                            onCaptionCommit={commitCaption}
                            onPick={(range) => togglePick(item.id, range)}
                            onAnchor={() => noteTile(item.id)}
                            onTileEdit={onTileEdit}
                            onDragStart={() => { dragRef.current = item.id; }}
                            onDragOver={() => setOverId(item.id)}
                            onDrop={() => dropOn(item.id)}
                            onDragEnd={() => { dragRef.current = null; setOverId(null); }}
                        />
                        )
                    ))}
                </div>
            ))}
        </section>
    );
}

ImagesMasonry.propTypes = {
    images: PropTypes.array,
    url: PropTypes.string,
    hide: PropTypes.bool,
    columns: PropTypes.number, // Base column count from database (optional, will use content.home.columns if not provided)
    breakpoints: PropTypes.object, // Older width breakpoints; they now only narrow tablet and phone columns
    gap: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    /** Tablet and phone columns and gap that differ from desktop's. */
    devices: PropTypes.object,
    crops: PropTypes.object, // Crop data object keyed by image ID { [imageId]: { x, y, width, height } }
    /** While a gallery block is open in the preview, tiles can be dragged into a new order. */
    arrange: PropTypes.bool,
    onArrange: PropTypes.func,
    /** Tile tools (crop, replace, remove, …) while arranging in the Appearance preview. */
    onTileEdit: PropTypes.func,
    onFileDrop: PropTypes.func,
};

/**
 * Individual masonry image with preserved aspect ratio and lazy loading
 */
function MasonryImage({
    file,
    pageUrl,
    tabIndex,
    crop = null,
    arrange = false,
    over = false,
    picked = false,
    sharedCaption = null,
    onCaptionType,
    onCaptionCommit,
    onPick,
    onAnchor,
    onTileEdit,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
}) {
    const { id, dimensions, description } = file;
    const analytics = usePreviewImageAnalytics(id);
    const inEditor = useEditorFrame();
    const isVideo = getMediaType(file) === 'video';
    const suffix = isVideo ? 'vid' : 'img';
    const linkTo = pageUrl ? `/${pageUrl}/${id}.${suffix}` : `/${id}.${suffix}`;
    const replaceInputRef = React.useRef(null);
    const captionRef = React.useRef(description || '');
    
    React.useEffect(() => {
        captionRef.current = description || '';
    }, [description]);

    // A caption typed on another picked tile shows here at once, and stays
    // after the selection ends until the saved caption comes back.
    const [heldCaption, setHeldCaption] = React.useState(null);
    React.useEffect(() => {
        if (sharedCaption === null) return;
        captionRef.current = sharedCaption;
        setHeldCaption(sharedCaption);
    }, [sharedCaption]);
    React.useEffect(() => {
        setHeldCaption(null);
    }, [description]);
    const shownCaption = sharedCaption ?? heldCaption;
    
    // Calculate aspect ratio - use cropped aspect ratio if crop is present
    const aspectRatio = crop
        ? getCroppedAspectRatio(dimensions, crop)
        : (dimensions?.width && dimensions?.height
            ? `${dimensions.width} / ${dimensions.height}`
            : '1 / 1');
    
    const [seen] = React.useState(() => loadedTiles.has(id));
    const [isLoaded, setIsLoaded] = React.useState(seen);
    const [isInView, setIsInView] = React.useState(seen);
    const markLoaded = React.useCallback(() => {
        loadedTiles.add(id);
        setIsLoaded(true);
    }, [id]);
    const instant = seen ? { priority: true, decoding: 'sync' } : null;
    const fadeClass = seen ? '' : 'transition-opacity duration-300';
    const [focusedCaption, setFocusedCaption] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [posterIndex, setPosterIndex] = React.useState(0);
    const containerRef = React.useRef(null);
    const linkRef = React.useRef(null);
    const modifierPickRef = React.useRef(false);
    const cycleTimerRef = React.useRef(null);
    const warmedVideoRef = React.useRef(false);

    const posterUrls = React.useMemo(() => {
        if (!isVideo) return [];
        return getPosterPaths(file)
            .map((path) => toPublicMediaUrl(path))
            .filter(Boolean);
    }, [isVideo, file]);

    const activePosterUrl = posterUrls[posterIndex] || posterUrls[0] || null;
    const videoUrl = isVideo ? getVideoUrl(file) : null;

    React.useEffect(() => {
        if (!containerRef.current || isInView) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.disconnect();
                    }
                });
            },
            { rootMargin: '50px', threshold: 0.01 },
        );
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined' || !linkRef.current) return;
        const focusImageId = sessionStorage.getItem('focusImageId');
        if (focusImageId !== id) return;
        sessionStorage.removeItem('focusImageId');
        setTimeout(() => linkRef.current?.focus(), 100);
    }, [id]);

    React.useEffect(() => {
        if (cycleTimerRef.current) {
            clearInterval(cycleTimerRef.current);
            cycleTimerRef.current = null;
        }
        if (!isVideo || !isHovered || posterUrls.length < 2) {
            if (!isHovered) setPosterIndex(0);
            return undefined;
        }
        cycleTimerRef.current = setInterval(() => {
            setPosterIndex((prev) => (prev + 1) % posterUrls.length);
        }, 700);
        return () => {
            if (cycleTimerRef.current) {
                clearInterval(cycleTimerRef.current);
                cycleTimerRef.current = null;
            }
        };
    }, [isVideo, isHovered, posterUrls.length]);

    const warmVideoStart = React.useCallback(() => {
        if (!isVideo || !videoUrl || warmedVideoRef.current || typeof document === 'undefined') return;
        warmedVideoRef.current = true;
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'video';
        preload.href = videoUrl;
        document.head.appendChild(preload);
    }, [isVideo, videoUrl]);

    return (
        <figure
            ref={containerRef}
            className={cx(
                'masonry-item group m-0 relative',
                arrange && 'cursor-grab [&_img]:pointer-events-none',
                over && 'ring-2 ring-purple-500',
            )}
            data-image-id={id}
            data-scroll-to={id}
            data-page-path="page.tile"
            data-page-id={id}
            draggable={arrange || undefined}
            onPointerDown={(event) => {
                if (!arrange || event.button !== 0) return;
                if (isTileControl(event.target)) return;
                if (!event.shiftKey && !event.metaKey && !event.ctrlKey) return;
                // Settle the pick before a drag can start, so ⌘/Shift-click stays a selection.
                modifierPickRef.current = true;
                event.preventDefault();
                event.stopPropagation();
                onPick?.(event.shiftKey);
            }}
            onDragStart={(event) => {
                if (!arrange) return;
                if (isTileControl(event.target) || event.shiftKey || event.metaKey || event.ctrlKey) {
                    event.preventDefault();
                    return;
                }
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', id);
                setScaledDragImage(event, event.currentTarget);
                onAnchor?.();
                onDragStart?.();
            }}
            onDragOver={(event) => {
                if (dragHasFiles(event)) return;
                if (!arrange) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                onDragOver?.();
            }}
            onDrop={(event) => {
                if (dragHasFiles(event)) return;
                if (!arrange) return;
                event.preventDefault();
                onDrop?.();
            }}
            onDragEnd={() => onDragEnd?.()}
        >
            <Link
                ref={linkRef}
                to={linkTo}
                draggable={false}
                className="block group relative focus:outline-none"
                aria-label={description || (isVideo ? 'Play video' : 'View image')}
                tabIndex={tabIndex !== undefined ? tabIndex : 0}
                onMouseEnter={() => {
                    setIsHovered(true);
                    warmVideoStart();
                }}
                onMouseLeave={() => setIsHovered(false)}
                onFocus={warmVideoStart}
                onClick={arrange ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const modified = event.shiftKey || event.metaKey || event.ctrlKey;
                    if (modified) {
                        if (!modifierPickRef.current && !isTileControl(event.target)) onPick?.(event.shiftKey);
                        modifierPickRef.current = false;
                        return;
                    }
                    modifierPickRef.current = false;
                    if (isTileControl(event.target)) return;
                    onAnchor?.();
                } : undefined}
                onDoubleClick={arrange ? (event) => {
                    if (isTileControl(event.target) || event.shiftKey || event.metaKey || event.ctrlKey) return;
                    event.preventDefault();
                    event.stopPropagation();
                    onTileEdit?.({ action: 'ui-crop', kind: 'image', id });
                } : undefined}
            >
                <div
                    className="image-container relative w-full overflow-hidden"
                    style={{ aspectRatio }}
                >
                    <div
                        className={isLoaded ? 'hidden' : 'absolute inset-0'}
                        style={{ backgroundColor: 'color-mix(in srgb, var(--color-text) 6%, transparent)' }}
                        aria-hidden="true"
                    />
                    {isInView && !isVideo && (
                        <CroppedImage
                            image={file}
                            crop={crop}
                            alt={description || ''}
                            sizes="(max-width: 350px) 100vw, (max-width: 700px) 50vw, (max-width: 1000px) 33vw, (max-width: 1300px) 25vw, 20vw"
                            onLoad={markLoaded}
                            {...instant}
                            className={cx(
                                fadeClass,
                                isLoaded ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                    )}
                    {isInView && isVideo && activePosterUrl && (
                        <img
                            src={activePosterUrl}
                            alt={description || ''}
                            loading={seen ? 'eager' : 'lazy'}
                            decoding={seen ? 'sync' : undefined}
                            onLoad={markLoaded}
                            className={cx(
                                'h-full w-full object-cover',
                                fadeClass,
                                isLoaded ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                    )}
                    {isInView && isVideo && !activePosterUrl && (
                        <CroppedImage
                            image={file}
                            crop={crop}
                            alt={description || ''}
                            sizes="(max-width: 350px) 100vw, (max-width: 700px) 50vw, (max-width: 1000px) 33vw, (max-width: 1300px) 25vw, 20vw"
                            onLoad={markLoaded}
                            {...instant}
                            className={cx(
                                fadeClass,
                                isLoaded ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                    )}
                    {!arrange && description && (
                        <figcaption className="image-description pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus:opacity-100">
                            <span
                                className="absolute inset-0"
                                style={{ backgroundColor: 'var(--color-background)', opacity: 0.7 }}
                            />
                            <span className="grid-caption relative z-10 text-center">
                                {description}
                            </span>
                        </figcaption>
                    )}
                    {hasAnalyticsData(analytics) && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/45 to-transparent px-2 pb-1.5 pt-6 opacity-75 transition-opacity duration-200 group-hover:opacity-100">
                            <AnalyticsMetricStack analytics={analytics} numbersOnly />
                        </div>
                    )}
                </div>
            </Link>
            {inEditor && !arrange && (
                <div className="pointer-events-none absolute right-1 top-1 z-30 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                    <DownloadOriginalButton file={file} tone="onImage" />
                </div>
            )}
            {arrange && (
                <div className="pointer-events-none absolute inset-0 z-30">
                    <div className="absolute left-0 top-0 z-10 flex items-start text-xs">
                        <span className="block bg-white/80 px-2 py-1.5 font-medium text-gray-900">
                            {tabIndex || 1}
                        </span>
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={picked}
                            aria-label={picked ? 'Unselect image' : 'Select image'}
                            title="Select. ⌘-click the image selects it. Shift-click selects every image in between."
                            className={cx(
                                'pointer-events-auto ml-1 mt-1 flex h-5 w-5 items-center justify-center rounded-full border-2 shadow transition-colors',
                                picked ? 'border-[#2563eb] bg-[#2563eb] text-white' : 'border-white bg-black/30 text-transparent hover:bg-black/50',
                            )}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onPick?.(event.shiftKey);
                            }}
                        >
                            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </button>
                    </div>
                    {picked && (
                        <div className="absolute inset-0 z-[2] bg-[#2563eb]/15 ring-2 ring-inset ring-[#2563eb]" />
                    )}
                    <div className="pointer-events-auto">
                        <ImageTileActionButtons
                            hasCrop={Boolean(crop)}
                            hideNarrow={false}
                            downloadFile={file}
                            onLightbox={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'ui-open', kind: 'image', id });
                            }}
                            onReplace={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                replaceInputRef.current?.click();
                            }}
                            onCrop={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'ui-crop', kind: 'image', id });
                            }}
                            onRegenerate={frameFileTools() ? (event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'ui-regenerate', kind: 'image', id });
                            } : undefined}
                            onRemove={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'remove', kind: 'image', id });
                            }}
                            replaceInputRef={replaceInputRef}
                            onReplaceFileChange={(event) => {
                                const fileObj = event.target.files?.[0];
                                event.target.value = '';
                                if (!fileObj) return;
                                onTileEdit?.({ action: 'ui-replace', kind: 'image', id, file: fileObj });
                            }}
                        />
                    </div>
                    <div className="pointer-events-none absolute inset-0 hidden sm:block" data-tile-control>
                        <DescriptionInput
                            tabIndex={tabIndex}
                            defaultValue={description || ''}
                            value={shownCaption ?? undefined}
                            onChange={(next) => {
                                captionRef.current = next;
                                onCaptionType?.(id, next);
                            }}
                            onFocus={() => setFocusedCaption(true)}
                            onBlur={() => {
                                setFocusedCaption(false);
                                onCaptionCommit?.(id, captionRef.current);
                            }}
                            overlay
                            overlayVariant="whiteOnBlack"
                        />
                    </div>
                    {focusedCaption && (
                        <div className="pointer-events-none absolute inset-0 z-[1] bg-white/40" />
                    )}
                </div>
            )}
        </figure>
    );
}

MasonryImage.propTypes = {
    file: PropTypes.object.isRequired,
    pageUrl: PropTypes.string,
    tabIndex: PropTypes.number,
    crop: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    arrange: PropTypes.bool,
    over: PropTypes.bool,
    /** Picked for a bulk action while arranging. */
    picked: PropTypes.bool,
    /** The caption being typed on any picked tile, shown on every picked tile; null when none. */
    sharedCaption: PropTypes.string,
    /** (id, text) on each keystroke in this tile's caption. */
    onCaptionType: PropTypes.func,
    /** (id, text) when this tile's caption loses focus. */
    onCaptionCommit: PropTypes.func,
    /** (range: boolean) — range picks every tile since the last one clicked or dragged. */
    onPick: PropTypes.func,
    /** Remember this tile as the start of the next shift-click, without changing the selection. */
    onAnchor: PropTypes.func,
    onTileEdit: PropTypes.func,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    onDragEnd: PropTypes.func,
};

export default ImagesMasonry;
